import debugConstructor from 'debug'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { l } from '@manychat/manyui'

import * as builderActions from 'common/builder/actions/builderActions'
import * as builderAsyncActions from 'common/builder/actions/builderAsyncActions'
import {
  tryToPreActivateTrigger,
  tryOpenFirstAutomationCreationModal,
} from 'common/builder/actions/manager/managerActionsTyped'
import { setBusy } from 'common/builder/actions/setBusy'
import { updateBuilderState } from 'common/builder/actions/updateBuilderState'
import * as atypes from 'common/builder/constants/builderReduxActionTypes'
import { MessagingType, FBMessagingType } from 'common/builder/constants/facebookMessaging'
import { Namespace } from 'common/builder/constants/Namespace'
import { NodeType } from 'common/builder/constants/NodeType'
import ManagerStateModel from 'common/builder/models/ManagerState'
import builderSelectors from 'common/builder/selectors/builder'
import * as managerSelectors from 'common/builder/selectors/managerSelectors'
import { alertChannelRequired } from 'common/builder/shortcuts'
import { hasFBNodes, hasSMSNodes, hasEmailNodes } from 'common/builder/utils/hasChannelNodes'
import { sendPublishErrorToAnalytics } from 'common/builder/utils/sendPublishErrorToAnalytics'
import { SetUpAiAddonListeners } from 'common/core/actions/setUpAiAddonListenersActions'
import { ChannelType } from 'common/core/constants/ChannelType'
import {
  getIsEmailNodeEnabled,
  hasConnectedSMSChannel,
  isFBChannelConnected,
} from 'common/core/selectors/channelSelectors'
import { resetFlow } from 'common/flow/actions/flowActions'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'
import * as API from 'constants/API'
import { WelcomeMessageApi } from 'shared/api/requests/welcomeMessage'
import mcApi from 'utils/api/mcApi'
import { getClientId } from 'utils/clientId'
import localStorage from 'utils/localStorage'
import { navigatePollyfill } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'
import { accountFlags } from 'utils/services/featureFlagsService'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { alertWithHotjarEventFactory } from 'utils/services/hotjar'

import * as autoSaveActions from './autoSaveActions'
import * as managerFlowActions from './managerFlowActions'
import * as managerStateActions from './managerStateActions'

const debug = debugConstructor('manager')

const alertWithHotjarEvent = alertWithHotjarEventFactory('flow_builder_error')

/**
 * Manager initialization options
 * @typedef {object} ManagerOptions
 * @property {string} chId
 * @property {string} defaultChId
 * @property {string} flowId
 * @property {boolean} viewerMode
 * @property {BuilderOptions} builderOptions
 */

/**
 * Creates new ManagerState
 * @param {ManagerOptions} options
 */
export function createManagerState(options = {}) {
  return (dispatch, getState) => {
    debug('createManagerState', options)

    const {
      flowId,
      managerId,
      defaultChId,
      viewerMode,
      builderOptions = {},
      independentMode = false,
    } = options

    if (viewerMode) {
      builderOptions.loadStats = true
    }

    const initial = {
      flowId,
      viewerMode: Boolean(viewerMode),
      builderOptions,
    }

    if (managerId) {
      initial.id = managerId
    }

    const item = ManagerStateModel.create(initial)
    dispatch({ type: atypes.CREATE_MANAGER_STATE, item })

    if (independentMode) {
      dispatch(managerStateActions.createIndependentBuilder(item.id))
    } else {
      dispatch(
        managerStateActions.fetchFlowAndCreateBuilder(item.id, {
          chId: defaultChId,
        }),
      )
    }

    return item
  }
}

/**
 * @param {string} managerId
 * @param {Object} options
 * @param {boolean} options.tryAutoSave
 */
export function removeManager(managerId, options = { tryAutoSave: true }) {
  return (dispatch) => {
    dispatch(managerStateActions.removeBuilder(managerId, options))
    dispatch(managerStateActions.removeManagerState(managerId))
  }
}

/**
 * @param managerId
 */
export function discardDraft(managerId) {
  return async (dispatch, getState) => {
    dispatch(managerStateActions.removeBuilder(managerId))
    await dispatch(managerFlowActions.discardFlowChanges(managerId))
    return dispatch(managerStateActions.fetchFlowAndCreateBuilder(managerId))
  }
}

/**
 * @param managerId
 */
export function save(managerId) {
  return async (dispatch) => {
    dispatch(autoSaveActions.tryAutoSave(managerId, { force: true }))
  }
}

/**
 * Publishes posts and main menus
 * @param {string} managerId
 * @param {Object} [options]
 * @param {boolean} options.silent
 * @returns {import('common/core/coreInterfaces').IThunkAction}
 */
export function publish(managerId, options = {}) {
  return async (dispatch, getState, context) => {
    const state = getState()
    const manager = managerSelectors.getById(state, managerId)

    if (!manager) {
      return false
    }
    const builderId = manager.builderId

    const builder = builderSelectors.builderState.getById(state, builderId)

    analyticsService.sendEvent(`BUILDER.PUBLISH_ATTEMPT`, { pathname: document.location.pathname })

    if (builder.isBusy) {
      return false
    }

    dispatch({ type: atypes.BUILDER_PUBLISH_CLICKED, builderId })
    dispatch(SetUpAiAddonListeners({ managerId }))

    let { valid, hideSidebar = false } = dispatch(builderActions.validate(builderId))
    if (localStorage.getItem('mcht_disable_builder_publish_validation')) {
      valid = true
    }

    if (!valid) {
      dispatch(updateBuilderState(builderId, { sidebarHidden: hideSidebar }))
      return false
    }

    try {
      dispatch(setBusy(builderId, true))
      dispatch(managerStateActions.updateManagerState(manager.id, { isPublishing: true }))

      const batch = builderSelectors.builderState.getBatch(state, builderId)
      const managerFlow = flowSelectors.getById(state, manager.flowId)

      const data = await mcApi.post(API.flow.endpoints.publishFlow, {
        body: {
          ns: manager.flowId,
          batch,
          coordinates: builder.coordinates,
          // for retrieving aggregated stats content holder should be omitted, not empty, and not null
          ...(managerFlow.chId ? { content_holder: managerFlow.chId } : {}),
          // IMPORTANT: we use manager id as a client id here, @see flowNotificationActions.js
          client_id: `${getClientId()}|${manager.id}`,
        },
      })

      const doesManagerExist = Boolean(managerSelectors.getById(getState(), managerId))
      if (!doesManagerExist) {
        return
      }

      dispatch(resetFlow(data.flow))
      dispatch(managerStateActions.applyBuilderBatch(managerId, get(data, 'flow.contents')))

      if (manager.flowId === Namespace.WELCOME_MESSAGE) {
        await WelcomeMessageApi.register()
      }

      if (!options.silent) {
        const { flow } = data
        const showFBWarning = !isFBChannelConnected(state) && hasFBNodes(flow)
        const showSMSWarning = !hasConnectedSMSChannel(state) && hasSMSNodes(flow)
        const showEmailWarning = !getIsEmailNodeEnabled(state) && hasEmailNodes(flow)
        if (showFBWarning) {
          alertChannelRequired(ChannelType.FB)
        }
        if (showSMSWarning) {
          alertChannelRequired(ChannelType.SMS)
        }
        if (showEmailWarning) {
          alertChannelRequired(ChannelType.EMAIL)
        }

        await dispatch(tryOpenFirstAutomationCreationModal({ managerId }))
        dispatch(tryToPreActivateTrigger(flow, builderId, !managerFlow.has_published_content))
        dispatch(
          updateBuilderState(manager.builderId, {
            isDeleteSubscriberActionConfirmed: false,
          }),
        )
      }

      const isFBMarketingMessagesEnabled = accountFlags.isEnabled(
        AccountFlag.ENABLE_FB_MARKETING_MESSAGES,
      )
      const firstNode = builderSelectors.builderState.getFirstNode(getState(), builderId)

      if (
        isFBMarketingMessagesEnabled &&
        firstNode?.nodeType === NodeType.CONTENT &&
        firstNode?.$fbMessagingType === FBMessagingType.OUTSIDE_24_HOURS
      ) {
        const fbmmSettings = firstNode?.fbmmSettings
        const messageType = fbmmSettings
          ? MessagingType.MARKETING_MESSAGE
          : MessagingType.RECURRING_NOTIFICATION

        analyticsService.sendEvent(`BUILDER.PUBLISH_SUCCESS`, {
          pathname: document.location.pathname,
          messageType,
          fbmmSettings,
        })
      } else {
        analyticsService.sendEvent(`BUILDER.PUBLISH_SUCCESS`, {
          pathname: document.location.pathname,
        })
      }

      dispatch({ type: atypes.REMOVE_ALL_BUILDER_SNAPSHOTS, builderId })
      return true
    } catch (err) {
      console.trace(err)
      const errorResponse = err?.response
      const contentNodeErrors = get(errorResponse, 'content_node_errors')
      const originalContentNodeErrors = errorResponse?.original_content_node_errors ?? {}
      const firstOriginalError = Object.values(originalContentNodeErrors)[0] || 'Publish error'

      if (!isEmpty(contentNodeErrors)) {
        dispatch(updateBuilderState(builderId, { sidebarHidden: false }))
        dispatch(builderActions.showServerErrors(builderId, contentNodeErrors))
        sendPublishErrorToAnalytics(firstOriginalError)
      } else {
        const errorMessage = errorResponse?.error || errorResponse?.errors?.join(', ')
        sendPublishErrorToAnalytics(errorMessage || 'Publish error')
        alertWithHotjarEvent(errorMessage || l.translate('Publish error'), 'danger')
        errorTrackingService.trackError(err)
      }
    } finally {
      const doesManagerExist = Boolean(managerSelectors.getById(getState(), managerId))
      if (doesManagerExist) {
        dispatch(setBusy(builderId, false))
        dispatch(managerStateActions.updateManagerState(manager.id, { isPublishing: false }))
      }
    }
    return false
  }
}

/**
 * @param managerId
 */
export function sendPreview(managerId, options = {}) {
  return async (dispatch, getState) => {
    const state = getState()
    const manager = managerSelectors.getById(state, managerId)

    if (!manager) {
      return
    }

    const { valid } = dispatch(
      builderActions.validate(manager.builderId, { ignoreProErrors: true }),
    )
    if (!valid) {
      dispatch(updateBuilderState(manager.builderId, { sidebarHidden: false }))
      return
    }

    dispatch(managerStateActions.updateManagerState(manager.id, { isSendingPreview: true }))

    const isSuccess = await dispatch(builderAsyncActions.sendPreview(manager.builderId, options))
    if (!isSuccess) {
      dispatch(updateBuilderState(manager.builderId, { sidebarHidden: false }))
    }

    dispatch(managerStateActions.updateManagerState(manager.id, { isSendingPreview: false }))
  }
}

/**
 * @param managerId
 * @param url
 */
export function publishAndRedirect(managerId, url) {
  return async (dispatch) => {
    const result = await dispatch(publish(managerId))
    if (result && url) {
      navigatePollyfill(url)
    }
  }
}
